import { SortableContext, useSortable } from "@dnd-kit/sortable";
import { useCallback, useEffect, useMemo, useState } from "react";
import TaskCard from "./TaskCard";
import styled from "styled-components";
import { Column, Task } from "./Kanban";
import tinycolor from "tinycolor2";
import { useInView } from "react-intersection-observer";

export type Id = string | number;

const Container = styled.div<{ heigthfilter: number }>`
    background-color: var(--bg-color-kanban);
    width: 350px;
    min-width: 350px;
    height: ${({ heigthfilter }) => `calc(100vh - ${heigthfilter}px)`};
    max-height: auto;
    border-radius: 0.375rem;
    display: flex;
    flex-direction: column;
    flex-grow: 1;
`;

const ContainerTitle = styled.div`
    font-size: 1rem; 
    height: 55px;
    border-radius: 11px;
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
    padding: 0.75rem; 
    font-weight: bold;
    border: 8px solid var(--bg-color-kanban);
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    user-select: none;
`;

const Title = styled.div<{ color: string }>`
    display: flex;
    flex: 1;
    gap: 0.5rem;     
    color: ${(props) => props.color};
`;

const Icon = styled.div<{ color: string }>`
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: ${(props) => props.color};
    padding: 0.15rem 0.3rem; 
    font-size: 20px; 
    border-radius: 9999px; 
`;

const Wrapper = styled.div`
    display: flex;
    flex-grow: 1;
    flex-direction: column;
    gap: 1rem;
    padding: 0.5rem; 
    overflow-x: hidden;
    overflow-y: auto;

    &::-webkit-scrollbar {
        width: 4px;
    }

    &::-webkit-scrollbar-thumb {
        background-color: rgb(172, 172, 172); 
        border-radius: 6px; 
    }

    &::-webkit-scrollbar-thumb:hover {
        background-color: #999; 
    }
`;

export interface ColumnContainerProps {
    column: Column;
    updateTask: (id: Id, content: string) => void;
    deleteTask: (id: Id) => void;
    tasks: Task[];
}

function ColumnContainer({ column, tasks, deleteTask, updateTask, }: ColumnContainerProps) {
    const { ref, inView } = useInView({ triggerOnce: false });
    const [limit, setLimit] = useState(50)
    const [heigthFilter, setHeigthFilter] = useState(0);

    const tasksIds = useMemo(() => {
        return tasks.map((task) => task.id);
    }, [tasks]);

    const { setNodeRef } = useSortable({
        id: column.id,
        data: {
            type: "Column",
            column,
        },
    });

    useEffect(() => {
        const filter = document.getElementById("filterkanban");
        const toolbar = document.getElementById("principal-toolbar");

        const filter_heigth = filter?.getBoundingClientRect().height ?? 0;
        const toolbar_heigth = toolbar?.getBoundingClientRect().height ?? 0;

        setHeigthFilter(filter_heigth + toolbar_heigth + 20);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    var textColor = 'var(--font-color)';

    if (column.color)
        textColor = tinycolor(column.color).isDark() ? '#EAF4FD' : '#212529'

    const loadMore = useCallback(() => {
        setLimit(e => e + 50)
    }, []);

    useEffect(() => {
        if (inView) loadMore();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inView])

    return (
        <Container ref={setNodeRef} heigthfilter={heigthFilter}>
            <ContainerTitle style={{ backgroundColor: column.color }}>
                <Title color={textColor}>
                    <Icon style={{ color: column.color }} color={textColor}>{column.icon}</Icon>
                    {column.title}
                    <div style={{ flex: 1 }}></div>
                    {tasks.length ?? 0}
                </Title>
            </ContainerTitle>

            <Wrapper>
                <SortableContext items={tasksIds}>
                    {tasks.filter((e, index) => index < limit).map((task) => (
                        <TaskCard
                            key={task.id}
                            task={task}
                            deleteTask={deleteTask}
                            updateTask={updateTask}
                            color={column.color}
                        />
                    ))}

                    <div ref={ref} style={{ height: 50 }} />
                </SortableContext>
            </Wrapper>
        </Container>
    );
}

export default ColumnContainer;